import { useState } from 'react';
import { InterviewQuestion, QuestionCategory } from '@recruitai/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { useQuestionEditor } from '@/hooks/useQuestionEditor';

interface QuestionListEditorProps {
  jobId: string;
  candidateId: string;
  initialQuestions?: InterviewQuestion[];
  onSaved?: () => void;
}

const CATEGORY_LABELS: Record<QuestionCategory, string> = {
  [QuestionCategory.TECHNICAL]: 'Técnica',
  [QuestionCategory.BEHAVIORAL]: 'Comportamental',
  [QuestionCategory.SITUATIONAL]: 'Situacional',
  [QuestionCategory.CULTURE]: 'Cultura',
};

const CATEGORY_COLORS: Record<QuestionCategory, string> = {
  [QuestionCategory.TECHNICAL]: 'bg-blue-100 text-blue-800',
  [QuestionCategory.BEHAVIORAL]: 'bg-green-100 text-green-800',
  [QuestionCategory.SITUATIONAL]: 'bg-purple-100 text-purple-800',
  [QuestionCategory.CULTURE]: 'bg-orange-100 text-orange-800',
};

export function QuestionListEditor({ jobId, candidateId, initialQuestions, onSaved }: QuestionListEditorProps) {
  const {
    questions, isSaving, updateQuestion, removeQuestion, addQuestion, saveQuestions, copyToClipboard,
  } = useQuestionEditor(jobId, candidateId, initialQuestions);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [newCategory, setNewCategory] = useState<QuestionCategory>(QuestionCategory.TECHNICAL);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Agrupar por categoria
  const grouped = Object.values(QuestionCategory).reduce((acc, cat) => {
    acc[cat] = questions.filter((q) => q.category === cat);
    return acc;
  }, {} as Record<QuestionCategory, InterviewQuestion[]>);

  const handleAddQuestion = () => {
    if (!newQuestion.trim()) return;
    addQuestion({
      category: newCategory,
      question: newQuestion.trim(),
      rationale: '',
      whatToLookFor: '',
    });
    setNewQuestion('');
    setShowAddForm(false);
  };

  const handleSave = async () => {
    await saveQuestions();
    onSaved?.();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Perguntas da Entrevista ({questions.length})</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={copyToClipboard}>
            📋 Copiar Roteiro
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowAddForm(true)}>
            + Adicionar
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      {/* Formulário de adicionar */}
      {showAddForm && (
        <Card className="p-4 space-y-3">
          <div className="flex gap-2">
            <Select
              className="w-48"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value as QuestionCategory)}
            >
              {Object.values(QuestionCategory).map((cat) => (
                <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
              ))}
            </Select>
            <Input
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              placeholder="Digite a pergunta..."
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && handleAddQuestion()}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>Cancelar</Button>
            <Button size="sm" onClick={handleAddQuestion} disabled={!newQuestion.trim()}>Adicionar</Button>
          </div>
        </Card>
      )}

      {/* Perguntas agrupadas por categoria */}
      {Object.values(QuestionCategory).map((cat) => {
        const catQuestions = grouped[cat];
        if (catQuestions.length === 0) return null;

        return (
          <div key={cat} className="space-y-2">
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium px-2 py-1 rounded ${CATEGORY_COLORS[cat]}`}>
                {CATEGORY_LABELS[cat]}
              </span>
              <span className="text-xs text-muted-foreground">{catQuestions.length} pergunta(s)</span>
            </div>

            {catQuestions.map((q) => (
              <Card key={q.id} className="p-3 space-y-2">
                {editingId === q.id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={q.question}
                      onChange={(e) => updateQuestion(q.id, { question: e.target.value })}
                      rows={2}
                    />
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Concluir</Button>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm flex-1">{q.question}</p>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(q.id)}>✏️</Button>
                      <Button variant="ghost" size="sm" onClick={() => removeQuestion(q.id)}>🗑️</Button>
                    </div>
                  </div>
                )}

                {q.rationale && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">Racional:</span> {q.rationale}
                  </p>
                )}
                {q.whatToLookFor && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">O que avaliar:</span> {q.whatToLookFor}
                  </p>
                )}
              </Card>
            ))}
          </div>
        );
      })}

      {questions.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          Nenhuma pergunta gerada. Adicione perguntas manualmente.
        </p>
      )}
    </div>
  );
}
